import { fg } from '@atlaskit/platform-feature-flags';

import type { SearchPageConfig } from '../../types';
import { isContainedWithinMediaWrapper } from '../../vc-observer/media-wrapper/vc-utils';
import isDnDStyleMutation from '../../vc-observer/observers/non-visual-styles/is-dnd-style-mutation';
import isNonVisualStyleMutation from '../../vc-observer/observers/non-visual-styles/is-non-visual-style-mutation';
import { RLLPlaceholderHandlers } from '../../vc-observer/observers/rll-placeholders';
import { type VCObserverEntryType } from '../types';

import { createIntersectionObserver, type VCIntersectionObserver } from './intersection-observer';
import createMutationObserver from './mutation-observer';
import createPerformanceObserver from './performance-observer';
import { type MutationData, type MutationDataWithTimestamp } from './types';
import checkWithinComponent, { cleanupCaches } from './utils/check-within-component';
import { isContainedWithinSmartAnswers } from './utils/is-contained-within-smart-answers';
import { isElementVisible } from './utils/is-element-visible';
import isInVCIgnoreIfNoLayoutShiftMarker from './utils/is-in-vc-ignore-if-no-layout-shift-marker';
import { isInputNameMutation } from './utils/is-input-name-mutation';
import { isSameRectDimensions } from './utils/is-same-rect-dimensions';
import { isSameRectSize } from './utils/is-same-rect-size';

export type ViewPortObserverConstructorArgs = {
	onChange(onChangeArgs: {
		readonly time: DOMHighResTimeStamp;
		readonly type: VCObserverEntryType;
		readonly elementRef: WeakRef<HTMLElement>;
		readonly visible: boolean;
		readonly rect: DOMRectReadOnly;
		readonly previousRect: DOMRectReadOnly | undefined;
		readonly mutationData?: MutationData | undefined | null;
	}): void;
	getSSRState?: () => any;
	getSSRPlaceholderHandler?: () => any;
	trackLayoutShiftOffenders?: boolean;
	searchPageConfig?: SearchPageConfig;
};

const createElementMutationsWatcher =
	(
		removedNodeRects: (DOMRect | undefined)[],
		isWithinThirdPartySegment: boolean,
		isWithinSmartAnswersSegment: boolean,
		hasSameDeletedNode: boolean,
		timestamp: number,
		isTargetReactRoot: boolean,
		getSSRState?: () => any,
		getSSRPlaceholderHandler?: () => any,
	) =>
	({ target, rect }: { rect: DOMRectReadOnly; target: HTMLElement }): VCObserverEntryType => {
		if (getSSRState) {
			const ssrState = getSSRState();
			const SSRStateEnum = { normal: 1, waitingForFirstRender: 2, ignoring: 3 };

			if (
				ssrState.state === SSRStateEnum.waitingForFirstRender &&
				timestamp > ssrState.renderStart &&
				isTargetReactRoot
			) {
				ssrState.state = SSRStateEnum.ignoring;
				if (ssrState.renderStop === -1) {
					// arbitrary 500ms DOM update window
					ssrState.renderStop = timestamp + 500;
				}
				return 'ssr-hydration';
			}

			if (
				ssrState.state === SSRStateEnum.ignoring &&
				timestamp > ssrState.renderStart &&
				isTargetReactRoot
			) {
				if (timestamp <= ssrState.renderStop) {
					return 'ssr-hydration';
				} else {
					ssrState.state = SSRStateEnum.normal;
				}
			}
		}

		if (getSSRPlaceholderHandler) {
			const ssrPlaceholderHandler = getSSRPlaceholderHandler();
			if (ssrPlaceholderHandler) {
				if (
					(ssrPlaceholderHandler.isPlaceholderV4(target) ||
						ssrPlaceholderHandler.isPlaceholderIgnored(target)) &&
					ssrPlaceholderHandler.checkIfExistedAndSizeMatchingV3(target)
				) {
					return 'mutation:ssr-placeholder';
				}

				if (
					(ssrPlaceholderHandler.isPlaceholderReplacementV4(target) ||
						ssrPlaceholderHandler.isPlaceholderIgnored(target)) &&
					ssrPlaceholderHandler.validateReactComponentMatchToPlaceholderV4(target)
				) {
					return 'mutation:ssr-placeholder';
				}
			}
		}

		if (hasSameDeletedNode && isInVCIgnoreIfNoLayoutShiftMarker(target)) {
			return 'mutation:remount';
		}

		if (isContainedWithinMediaWrapper(target)) {
			return 'mutation:media';
		}

		if (isWithinThirdPartySegment) {
			return 'mutation:third-party-element';
		}

		if (isWithinSmartAnswersSegment) {
			return 'mutation:smart-answers-element';
		}

		const isInIgnoreLsMarker = isInVCIgnoreIfNoLayoutShiftMarker(target);

		if (!isInIgnoreLsMarker) {
			return 'mutation:element';
		}

		const isRLLPlaceholder = RLLPlaceholderHandlers.getInstance().isRLLPlaceholderHydration(rect);
		if (isRLLPlaceholder && isInIgnoreLsMarker) {
			return 'mutation:rll-placeholder';
		}

		const wasDeleted = removedNodeRects.some((nr) => isSameRectDimensions(nr, rect));

		if (wasDeleted && isInIgnoreLsMarker) {
			return 'mutation:element-replacement';
		}

		return 'mutation:element';
	};

const createElementMutationsWatcherNew =
	(
		removedNodeRects: (DOMRect | undefined)[],
		isWithinThirdPartySegment: boolean,
		isWithinSmartAnswersSegment: boolean,
		hasSameDeletedNode: boolean,
		timestamp: number,
		isTargetReactRoot: boolean,
		getSSRState?: () => any,
		getSSRPlaceholderHandler?: () => any,
	) =>
	({
		target,
		rect,
	}: {
		rect: DOMRectReadOnly;
		target: HTMLElement;
	}): { type: VCObserverEntryType; mutationData: MutationDataWithTimestamp } => {
		if (getSSRState) {
			const ssrState = getSSRState();
			const SSRStateEnum = { normal: 1, waitingForFirstRender: 2, ignoring: 3 };

			if (
				ssrState.state === SSRStateEnum.waitingForFirstRender &&
				timestamp > ssrState.renderStart &&
				isTargetReactRoot
			) {
				ssrState.state = SSRStateEnum.ignoring;
				if (ssrState.renderStop === -1) {
					// arbitrary 500ms DOM update window
					ssrState.renderStop = timestamp + 500;
				}
				return {
					type: 'ssr-hydration',
					mutationData: {
						timestamp,
					},
				};
			}

			if (
				ssrState.state === SSRStateEnum.ignoring &&
				timestamp > ssrState.renderStart &&
				isTargetReactRoot
			) {
				if (timestamp <= ssrState.renderStop) {
					return {
						type: 'ssr-hydration',
						mutationData: {
							timestamp,
						},
					};
				} else {
					ssrState.state = SSRStateEnum.normal;
				}
			}
		}

		if (getSSRPlaceholderHandler) {
			const ssrPlaceholderHandler = getSSRPlaceholderHandler();
			if (ssrPlaceholderHandler) {
				if (
					(ssrPlaceholderHandler.isPlaceholderV4(target) ||
						ssrPlaceholderHandler.isPlaceholderIgnored(target)) &&
					ssrPlaceholderHandler.checkIfExistedAndSizeMatchingV3(target)
				) {
					return {
						type: 'mutation:ssr-placeholder',
						mutationData: {
							timestamp,
						},
					};
				}

				if (
					(ssrPlaceholderHandler.isPlaceholderReplacementV4(target) ||
						ssrPlaceholderHandler.isPlaceholderIgnored(target)) &&
					ssrPlaceholderHandler.validateReactComponentMatchToPlaceholderV4(target)
				) {
					return {
						type: 'mutation:ssr-placeholder',
						mutationData: {
							timestamp,
						},
					};
				}
			}
		}

		if (hasSameDeletedNode && isInVCIgnoreIfNoLayoutShiftMarker(target)) {
			return {
				type: 'mutation:remount',
				mutationData: {
					timestamp,
				},
			};
		}

		if (isContainedWithinMediaWrapper(target)) {
			return {
				type: 'mutation:media',
				mutationData: {
					timestamp,
				},
			};
		}

		if (isWithinThirdPartySegment) {
			return {
				type: 'mutation:third-party-element',
				mutationData: {
					timestamp,
				},
			};
		}

		if (isWithinSmartAnswersSegment) {
			return {
				type: 'mutation:smart-answers-element',
				mutationData: {
					timestamp,
				},
			};
		}

		const isInIgnoreLsMarker = isInVCIgnoreIfNoLayoutShiftMarker(target);

		if (!isInIgnoreLsMarker) {
			return {
				type: 'mutation:element',
				mutationData: {
					timestamp,
				},
			};
		}

		const isRLLPlaceholder = RLLPlaceholderHandlers.getInstance().isRLLPlaceholderHydration(rect);
		if (isRLLPlaceholder && isInIgnoreLsMarker) {
			return {
				type: 'mutation:rll-placeholder',
				mutationData: {
					timestamp,
				},
			};
		}

		const wasDeleted = removedNodeRects.some((nr) => isSameRectDimensions(nr, rect));

		if (wasDeleted && isInIgnoreLsMarker) {
			return {
				type: 'mutation:element-replacement',
				mutationData: {
					timestamp,
				},
			};
		}

		return {
			type: 'mutation:element',
			mutationData: {
				timestamp,
			},
		};
	};

export default class ViewportObserver {
	private intersectionObserver: VCIntersectionObserver | null;
	private mutationObserver: MutationObserver | null;
	private performanceObserver: PerformanceObserver | null;
	private mapVisibleNodeRects: WeakMap<Element, DOMRect>;
	private mapIs3pResult: WeakMap<HTMLElement, boolean>;
	private onChange: ViewPortObserverConstructorArgs['onChange'];
	private isStarted: boolean;
	private trackLayoutShiftOffenders: boolean;
	private searchPageConfig: SearchPageConfig | undefined;
	private enableThirdPartyTracking: boolean | undefined;

	// SSR context functions
	private getSSRState?: () => any;
	private getSSRPlaceholderHandler?: () => any;

	constructor({
		onChange,
		getSSRState,
		getSSRPlaceholderHandler,
		trackLayoutShiftOffenders = false,
		searchPageConfig,
	}: ViewPortObserverConstructorArgs) {
		this.mapVisibleNodeRects = new WeakMap();
		this.mapIs3pResult = new WeakMap();
		this.onChange = onChange;
		this.isStarted = false;
		this.intersectionObserver = null;
		this.mutationObserver = null;
		this.performanceObserver = null;
		this.trackLayoutShiftOffenders = trackLayoutShiftOffenders;

		// Initialize SSR context functions
		this.getSSRState = getSSRState;
		this.getSSRPlaceholderHandler = getSSRPlaceholderHandler;
		this.searchPageConfig = searchPageConfig;
		this.enableThirdPartyTracking = fg('platform_ufo_reenable_3p_tracking');
	}

	private handleIntersectionEntry = ({
		target,
		rect,
		time,
		type,
		mutationData,
	}: {
		target: HTMLElement | null;
		rect: DOMRectReadOnly;
		time: DOMHighResTimeStamp;
		type: VCObserverEntryType;
		mutationData?: MutationData | null;
	}) => {
		if (!target) {
			return;
		}

		const visible = isElementVisible(target);
		const lastElementRect = this.mapVisibleNodeRects.get(target);
		this.mapVisibleNodeRects.set(target, rect);

		this.onChange({
			time,
			type,
			elementRef: new WeakRef(target),
			visible,
			rect,
			previousRect: lastElementRect,
			mutationData,
		});
	};

	private handleChildListMutation = async ({
		target,
		addedNodes,
		removedNodes,
		timestamp,
	}: {
		target: WeakRef<HTMLElement>;
		addedNodes: readonly WeakRef<HTMLElement>[];
		removedNodes: readonly WeakRef<HTMLElement>[];
		timestamp: DOMHighResTimeStamp;
	}) => {
		const removedNodeRects = removedNodes.map((ref) => {
			const n = ref.deref();

			if (!n) {
				return;
			}

			return this.mapVisibleNodeRects.get(n);
		});

		const targetNode = target.deref();

		for (const addedNodeRef of addedNodes) {
			const addedNode = addedNodeRef.deref();
			if (!addedNode) {
				continue;
			}

			const hasSameDeletedNode = removedNodes.find((ref) => {
				const n = ref.deref();
				if (!n || !addedNode) {
					return false;
				}
				return n.isEqualNode(addedNode);
			});

			const { isWithin: isWithinThirdPartySegment } = this.enableThirdPartyTracking
				? checkWithinComponent(addedNode, 'UFOThirdPartySegment', this.mapIs3pResult)
				: { isWithin: false };

			const isWithinSmartAnswersSegment = Boolean(
				this.shouldCheckSmartAnswersMutations() && isContainedWithinSmartAnswers(addedNode),
			);

			const isTargetReactRoot = targetNode === this.getSSRState?.()?.reactRootElement;

			this.intersectionObserver?.watchAndTag(
				addedNode,
				(this.trackLayoutShiftOffenders
					? createElementMutationsWatcherNew
					: createElementMutationsWatcher)(
					removedNodeRects,
					isWithinThirdPartySegment,
					isWithinSmartAnswersSegment,
					!!hasSameDeletedNode,
					timestamp,
					isTargetReactRoot,
					this.getSSRState,
					this.getSSRPlaceholderHandler,
				),
			);
		}
	};

	private handleAttributeMutation = ({
		target,
		attributeName,
		oldValue,
		newValue,
		timestamp,
	}: {
		target: HTMLElement;
		attributeName: string;
		oldValue?: string | undefined | null;
		newValue?: string | undefined | null;
		timestamp: DOMHighResTimeStamp;
	}) => {
		this.intersectionObserver?.watchAndTag(target, ({ target, rect }) => {
			if (isContainedWithinMediaWrapper(target)) {
				return {
					type: 'mutation:media',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
						timestamp,
					},
				};
			}

			if (fg('platform_ufo_exclude_3p_attribute_changes')) {
				const { isWithin: isWithinThirdPartySegment } = this.enableThirdPartyTracking
					? checkWithinComponent(target, 'UFOThirdPartySegment', this.mapIs3pResult)
					: { isWithin: false };
				if (isWithinThirdPartySegment) {
					return {
						type: 'mutation:third-party-attribute',
						mutationData: {
							attributeName,
							oldValue,
							newValue,
							timestamp,
						},
					};
				}
			}

			if (this.shouldCheckSmartAnswersMutations() && isContainedWithinSmartAnswers(target)) {
				return {
					type: 'mutation:smart-answers-attribute',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
					},
				};
			}

			if (isDnDStyleMutation({ target, attributeName, oldValue, newValue })) {
				return {
					type: 'mutation:attribute:non-visual-style',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
						timestamp,
					},
				};
			}

			if (isNonVisualStyleMutation({ target, attributeName, type: 'attributes' })) {
				return {
					type: 'mutation:attribute:non-visual-style',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
						timestamp,
					},
				};
			}

			if (isInputNameMutation({ target, attributeName, oldValue, newValue })) {
				return {
					type: 'mutation:attribute:non-visual-input-name',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
						timestamp,
					},
				};
			}

			const isRLLPlaceholder = RLLPlaceholderHandlers.getInstance().isRLLPlaceholderHydration(rect);
			if (isRLLPlaceholder) {
				return {
					type: 'mutation:rll-placeholder',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
						timestamp,
					},
				};
			}

			const lastElementRect = this.mapVisibleNodeRects.get(target);
			if (lastElementRect && isSameRectSize(rect, lastElementRect)) {
				return {
					type: 'mutation:attribute:no-layout-shift',
					mutationData: {
						attributeName,
						oldValue,
						newValue,
						timestamp,
					},
				};
			}

			return {
				type: 'mutation:attribute',
				mutationData: {
					attributeName,
					oldValue,
					newValue,
					timestamp,
				},
			};
		});
	};

	private handleLayoutShift = ({
		time,
		changedRects,
	}: {
		time: DOMHighResTimeStamp;
		changedRects: Array<{
			node: HTMLElement;
			rect: DOMRectReadOnly;
			previousRect: DOMRectReadOnly;
		}>;
	}) => {
		for (const changedRect of changedRects) {
			const target = changedRect.node;

			if (target) {
				const isSameCurrentAndPreviousRects = isSameRectDimensions(
					changedRect.rect,
					changedRect.previousRect,
				);
				this.onChange({
					time,
					elementRef: new WeakRef(target),
					visible: true,
					rect: changedRect.rect,
					previousRect: changedRect.previousRect,
					type: isSameCurrentAndPreviousRects ? 'layout-shift:same-rect' : 'layout-shift',
				});
			}
		}
	};

	private initializeObservers() {
		if (this.isStarted) {
			return;
		}

		this.intersectionObserver = createIntersectionObserver({
			onEntry: this.handleIntersectionEntry,
		});

		this.mutationObserver = createMutationObserver({
			onChildListMutation: this.handleChildListMutation,
			onAttributeMutation: this.handleAttributeMutation,
		});

		this.performanceObserver = createPerformanceObserver({
			onLayoutShift: this.handleLayoutShift,
		});
	}

	private shouldCheckSmartAnswersMutations = () => {
		return (
			this.searchPageConfig?.enableSmartAnswersMutations &&
			this.searchPageConfig?.searchPageRoute &&
			window?.location?.pathname &&
			window.location.pathname === this.searchPageConfig.searchPageRoute &&
			fg('rovo_search_page_ttvc_ignoring_smart_answers_fix')
		);
	};

	start(): void {
		if (this.isStarted) {
			return;
		}

		this.initializeObservers();

		this.mutationObserver?.observe(document.body, {
			attributeOldValue: true,
			attributes: true,
			childList: true,
			subtree: true,
		});

		this.performanceObserver?.observe({
			type: 'layout-shift',
			buffered: true,
			// @ts-ignore -error
			durationThreshold: 30,
		});

		this.isStarted = true;
	}

	stop(): void {
		if (!this.isStarted) {
			return;
		}

		this.mutationObserver?.disconnect();
		this.intersectionObserver?.disconnect();
		this.performanceObserver?.disconnect();
		this.isStarted = false;
		// Clean up caches when stopping
		cleanupCaches(this.mapIs3pResult);
	}
}
